import { Box, Typography, Chip, Tooltip, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ShowChart, Search, Block } from '@mui/icons-material';
import { thSx, tdSx, trSx, getTier, getFailLabel, getFailList, scoreColor, fmtDec } from './constants';
import { Card, CardTitle, ScoreBar, RiskBar, ComponentLabel, BreakoutBarBadge } from './primitives';
import StyleChip from './StyleChip';
import AIConfidenceBadge from '../../components/AIConfidenceBadge';

const RankingsTable = ({ filtered, total, search, setSearch, openHistory, sn, t }) => (
  <Card>
    <CardTitle
      icon={ShowChart}
      action={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            placeholder={t('stockStrength.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16 }} /></InputAdornment>,
              sx: { fontSize: '0.75rem', height: 32 },
            }}
            sx={{ width: 160 }}
          />
          <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>
            {filtered.length}/{total}
          </Typography>
        </Box>
      }
    >
      {t('stockStrength.sections.rankings')}
    </CardTitle>

    <TableContainer>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={thSx}>#</TableCell>
            <TableCell sx={thSx}>{t('stockStrength.table.ticker')}</TableCell>
            <TableCell sx={thSx} align="center">{t('stockStrength.table.score')}</TableCell>
            <TableCell sx={thSx} align="center">{t('stockStrength.table.aiConf', 'AI')}</TableCell>
            <TableCell sx={thSx} align="center">{t('stockStrength.table.style')}</TableCell>
            <TableCell sx={thSx} align="center">{t('stockStrength.table.rs')}</TableCell>
            <TableCell sx={thSx} align="center">{t('stockStrength.table.trend')}</TableCell>
            <TableCell sx={thSx} align="center">{t('stockStrength.table.mom')}</TableCell>
            <TableCell sx={thSx} align="center">{t('stockStrength.table.flow')}</TableCell>
            <TableCell sx={thSx} align="center">{t('stockStrength.table.resil')}</TableCell>
            <TableCell sx={thSx} align="center">{t('stockStrength.table.risk')}</TableCell>
            <TableCell sx={thSx} align="center">{t('stockStrength.table.tier')}</TableCell>
            <TableCell sx={thSx}>{t('stockStrength.table.missing')}</TableCell>
            <TableCell sx={thSx}>{t('stockStrength.table.sector')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.map((s, i) => (
            <TableRow key={s.ticker} hover onClick={() => openHistory(s.ticker)} sx={{ ...trSx(i), cursor: 'pointer' }}>
              <TableCell sx={tdSx}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8' }}>
                  {s.strength_rank}
                </Typography>
              </TableCell>
              <TableCell sx={tdSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <Typography sx={{
                    fontSize: '0.78rem', fontWeight: 700,
                    color: s.strength_tier === 'LEADER' ? '#10b981'
                         : s.strength_tier === 'EMERGING' ? '#3b82f6'
                         : '#1e293b',
                  }}>
                    {s.ticker}
                  </Typography>
                  {s.breakout_bar_flag && <BreakoutBarBadge t={t} />}
                  {s.manipulation_flag && (
                    <Tooltip title={t('stockStrength.manipulation.tooltip', { score: fmtDec(s.manipulation_score, 0) })}>
                      <Block sx={{ fontSize: 13, color: '#ef4444' }} />
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
              <TableCell sx={tdSx} align="center">
                <ScoreBar value={s.strength_score} color={scoreColor(s.level_keys?.score)} />
              </TableCell>
              <TableCell sx={tdSx} align="center">
                <AIConfidenceBadge
                  confidence={s.ai_confidence}
                  tooltipTitle={t(
                    'stockStrength.table.aiConfTooltip',
                    'AI confidence \u2014 pattern reliability win probability',
                  )}
                  compact
                />
              </TableCell>
              <TableCell sx={tdSx} align="center">
                <StyleChip style={s.trade_style} />
              </TableCell>
              <TableCell sx={tdSx} align="center">
                <ComponentLabel levelKey={s.level_keys?.rs}    value={s.components.relative_strength} tFn={t} />
              </TableCell>
              <TableCell sx={tdSx} align="center">
                <ComponentLabel levelKey={s.level_keys?.trend} value={s.components.trend_quality} tFn={t} />
              </TableCell>
              <TableCell sx={tdSx} align="center">
                <ComponentLabel levelKey={s.level_keys?.mom}   value={s.components.momentum_quality} tFn={t} />
              </TableCell>
              <TableCell sx={tdSx} align="center">
                <ComponentLabel levelKey={s.level_keys?.flow}  value={s.components.money_flow} tFn={t} />
              </TableCell>
              <TableCell sx={tdSx} align="center">
                <ComponentLabel levelKey={s.level_keys?.resil} value={s.components.resilience} tFn={t} />
              </TableCell>
              <TableCell sx={tdSx} align="center">
                <RiskBar levelKey={s.level_keys?.risk} value={s.components.risk_penalty} t={t} />
              </TableCell>
              <TableCell sx={tdSx} align="center">
                {(() => {
                  const tier = getTier(s.strength_tier);
                  return s.strength_tier && s.strength_tier !== 'REST' ? (
                    <Chip label={`${tier.icon} ${t('stockStrength.tiers.' + tier.labelKey)}`} size="small" sx={{
                      height: 20, fontSize: '0.6rem', fontWeight: 700,
                      bgcolor: alpha(tier.color, 0.08), color: tier.color,
                      border: `1px solid ${alpha(tier.color, 0.2)}`,
                    }} />
                  ) : s.persistent_leader ? (
                    <Chip label={t('stockStrength.tiers.persistent')} size="small" sx={{
                      height: 20, fontSize: '0.6rem', fontWeight: 600,
                      bgcolor: alpha('#8b5cf6', 0.08), color: '#8b5cf6',
                      border: `1px solid ${alpha('#8b5cf6', 0.2)}`,
                    }} />
                  ) : null;
                })()}
              </TableCell>
              <TableCell sx={tdSx}>
                {getFailList(s).length > 0 && (
                  <Typography sx={{ fontSize: '0.6rem', color: '#f59e0b', fontWeight: 500 }}>
                    {getFailList(s).map(f => getFailLabel(f, t)).join(', ')}
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={tdSx}>
                <Tooltip title={s.sector || ''}>
                  <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                    {sn(s.sector)}
                  </Typography>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Card>
);

export default RankingsTable;
